// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@adl/App/Components/Common/XngZone";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { color, xScreen } from "@adl/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductListLayoutStyles {
    container?: GridContainerProps;
    columnStyles?: {
        leftColumnGridItem?: GridItemProps;
        rightColumnGridItem?: GridItemProps;
    };
    mainSectionStyles?: {
        wrapper?: InjectableCss;
        actionsContainer?: InjectableCss;
        actionsContainerLeftSide?: InjectableCss;
        actionsContainerRightSide?: InjectableCss;
    };
    mobileStyles?: {
        wrapper?: InjectableCss;
        contentsWrapper?: InjectableCss;
    };
    modal?: ModalPresentationProps;
    filterWrapper?: GridItemProps;
    sortSection?: InjectableCss;
}

export const layoutStyles: ProductListLayoutStyles = {};

const styles = layoutStyles;

const ProductListLayout: FC<WidgetProps> = ({ id }) => {
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const shellContext = useShellContext();

    const modalOpenHandler = () => {
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    if (isTabletScreen) {
        return (
            <StyledWrapper {...styles.mobileStyles?.wrapper}>
                <XngZone contentId={id} zoneName="ContentMainHeader01" hint="Title" />
                <StyledWrapper {...styles.mobileStyles?.contentsWrapper}>
                    <XngZone contentId={id} zoneName="ContentMainHeader03" hint="Count Item" normalWidth={true} />
                    <Button variant="secondary" sizeVariant="small" onClick={modalOpenHandler}>
                        {translate("Sort & Filter")}
                    </Button>
                    {createWidgetElement("ProductList/ProductListFilterList", {
                        fields: {
                            showTitle: false,
                            showTags: true,
                        },
                    })}
                </StyledWrapper>
                <XngZone contentId={id} zoneName="ContentMainSection" hint="Main Section" />
                <Modal
                    {...styles.modal}
                    headline={translate("Sort & Filter")}
                    isOpen={modalIsOpen}
                    handleClose={modalCloseHandler}
                >
                    <GridContainer gap={8}>
                        <GridItem {...styles.filterWrapper}>
                            <XngZone contentId={id} zoneName="productsCount" hint="Count Section" />
                            <StyledWrapper {...styles.sortSection}>
                                <XngZone contentId={id} zoneName="ContentSort" hint="Sort Section" />
                            </StyledWrapper>
                            <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
                        </GridItem>
                    </GridContainer>
                </Modal>
            </StyledWrapper>
        );
    }
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.columnStyles?.leftColumnGridItem}>
                <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
            </GridItem>
            <GridItem {...styles.columnStyles?.rightColumnGridItem}>
                <XngZone contentId={id} zoneName="ContentMainHeader01" hint="Title" />
                <XngZone contentId={id} zoneName="ContentMainHeader02" hint="Suggestion" />
                <XngZone contentId={id} zoneName="ContentMainHeader03" hint="Count Item" />
                <StyledWrapper {...styles.mainSectionStyles?.actionsContainer}>
                    <StyledWrapper {...styles.mainSectionStyles?.actionsContainerLeftSide}>
                        <XngZone contentId={id} zoneName="ContentMainHeader04" hint="Sort Item" />
                        <XngZone
                            contentId={id}
                            zoneName="ContentMainHeader05"
                            normalWidth={true}
                            hint="Pagination Per Page"
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.mainSectionStyles?.actionsContainerRightSide}>
                        <XngZone contentId={id} zoneName="ContentMainHeader06" hint="View Item" />
                        <XngZone contentId={id} zoneName="ContentMainHeader07" hint="Pagination Page" />
                    </StyledWrapper>
                </StyledWrapper>
                <XngZone contentId={id} zoneName="ContentMainSection" hint="Main Section" />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: ProductListLayout,
    definition: {
        group: "Product List",
        displayName: "ProductList Layout",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
